import { Add16, Subtract16 } from '@carbon/icons-react';
import { Button, FormGroup, TextInput, TooltipDefinition } from 'carbon-components-react';
import clsx from 'clsx';
import useTimeBasedFeatureFlag, { FLAG_KEYS } from 'hooks/use-time-flag';
import { useFormContext } from 'react-hook-form';
import { dollarAmount } from 'util/format-number';
import { currencyInput } from 'util/validate-form';
import styles from './BidField.module.scss';
import { SUBSIDY_CAMPAIGN_KEYS } from './campaign-conf';

/**
 * BidField component renders a form field for setting bid amounts for campaigns.
 *
 * @param {Object} props - The component props.
 * @param {string} props.namespace - The namespace for the form field.
 * @param {boolean} props.disabled - Flag to disable the form field.
 * @param {boolean} props.isSubsidy - Flag to indicate if the campaign is a subsidy campaign.
 * @param {string} props.campaignKey - The key for the campaign. Sometimes the campaign's id or a temporary id as a row identifier.
 * @param {string} props.campaignShortName - The bucketed campaign type used to identify non custom campaigns.
 * @param {number} props.minBid - The minimum bid amount for the campaign.
 *
 * @returns {JSX.Element|null} The rendered BidField component.
 */

const AUTO_STATE_NAMESPACE = 'campaigns.auto.geography.state';
const { BRANDED_PREMIUM_SF, BRANDED_PREFERRED_SF } = SUBSIDY_CAMPAIGN_KEYS;

const BidField = ({ namespace, disabled, isSubsidy, campaignKey, campaignShortName, minBid }) => {
    return isSubsidy ? (
        <SubsidizedBidField
            namespace={namespace}
            disabled={disabled}
            campaignKey={campaignKey}
            campaignShortName={campaignShortName}
            minBid={minBid}
        />
    ) : (
        <NonSubBidField
            namespace={namespace}
            disabled={disabled}
            campaignKey={campaignKey}
            campaignShortName={campaignShortName}
            minBid={minBid}
        />
    );
};

const NonSubBidField = ({ namespace, disabled, campaignKey, campaignShortName, minBid }) => {
    const {
        register,
        formState: { errors },
        getValues,
        setValue
    } = useFormContext();
    const name = `${namespace}.${campaignKey}.bid`;
    const [root, product, section] = namespace.split('.');
    const campaignErrors = errors[root]?.[product]?.[section] || {};
    const currentCampaign = getValues(`${namespace}.${campaignKey}`);

    // Field with custom onChange and onBlur events
    const bid = register(name, {
        validate: (val) => {
            // only validate if the bid is enabled
            if (!currentCampaign?.enabled) return true;

            if (val === '') return `Bid is required.`;

            if (val < minBid) return `Min Bid: $${minBid.toFixed(2)}`;

            return true;
        }
    });

    if (!currentCampaign) {
        return null;
    }

    const getInvalidState = () => {
        if (campaignErrors[campaignKey]) {
            return !!campaignErrors[campaignKey]?.bid;
        }
        return false;
    };

    const getInvalidMessage = () => {
        if (campaignErrors[campaignKey]) {
            return campaignErrors[campaignKey]?.bid?.message;
        }
        return '';
    };

    return (
        <FormGroup
            className={clsx(styles.fieldset, styles.nonSubBidField)}
            disabled={!currentCampaign.enabled}
            legendText={`${currentCampaign.campaign} bid amount`}
        >
            <TextInput
                className={styles.bidInput}
                id={name}
                name={bid.name}
                ref={bid.ref}
                labelText={''}
                defaultValue={currentCampaign.bid}
                onChange={(evt) => {
                    bid.onChange(evt);
                    const { value } = evt.target;
                    evt.target.value = currencyInput(value);
                }}
                onBlur={(evt) => {
                    bid.onBlur(evt);
                    setValue(name, evt.target.value ? Number(evt.target.value).toFixed(2) : '', {
                        shouldDirty: true,
                        shouldValidate: true
                    });
                }}
                disabled={disabled}
                invalid={getInvalidState()}
                invalidText={getInvalidMessage()}
            />
        </FormGroup>
    );
};

const SubsidizedBidField = ({ namespace, disabled, campaignKey, campaignShortName, minBid }) => {
    const IS_AG_1583_ENABLED = useTimeBasedFeatureFlag(FLAG_KEYS.AG_1583);
    const { getValues, setValue } = useFormContext();
    const name = `${namespace}.${campaignKey}.bid`;
    const currentCampaign = getValues(`${namespace}.${campaignKey}`);
    const currentBid = Number(getValues(name));
    const { min, max } = currentCampaign.subsidy || {};

    const subsidyText =
        min === max ? `SF Subsidy: ${dollarAmount(min)}` : `SF Subsidy: ${dollarAmount(min)}-${dollarAmount(max)}`;
    let yourCostText = `Your Cost: ${calculateOutOfPocketCost(currentBid, max)}${
        min !== max ? `-${calculateOutOfPocketCost(currentBid, min)}` : ''
    }`;

    const updateBidValue = (updateAction) => {
        const nextBid = updateAction === BID_ACTION.INCREASE ? currentBid + 0.25 : currentBid - 0.25;
        setValue(name, nextBid.toFixed(2), { shouldDirty: true, shouldValidate: true });
    };

    // TODO: clean up check
    const autoStateIsCA = getValues(AUTO_STATE_NAMESPACE) === 'CA';
    const CAN_INCREMENT_BID = IS_AG_1583_ENABLED
        ? true
        : !(autoStateIsCA && [BRANDED_PREMIUM_SF, BRANDED_PREFERRED_SF].includes(campaignShortName));

    if (!currentCampaign) {
        return null;
    }

    const currentBidDisplay = <span className={styles.currentBidValue}>{dollarAmount(currentBid)}</span>;

    return (
        <FormGroup
            className={clsx(styles.fieldset, styles.subsidizedBidField)}
            disabled={!currentCampaign.enabled}
            legendText={`${currentCampaign.campaign} bid amount`}
        >
            {CAN_INCREMENT_BID && (
                <Button
                    disabled={currentBid <= minBid || disabled}
                    className={clsx(styles.bidActionButton, styles['bidActionButton--decrement'])}
                    hasIconOnly
                    iconDescription="Decrease bid $0.25"
                    onClick={() => updateBidValue(BID_ACTION.DECREASE)}
                    renderIcon={Subtract16}
                />
            )}

            {currentCampaign.subsidy ? (
                <TooltipDefinition
                    align="center"
                    direction="top"
                    tooltipText={
                        <div className={styles.subsidyTooltip}>
                            <div>{subsidyText}</div>
                            <div>{yourCostText}</div>
                            {min !== max && (
                                <div className={styles.subsidyTooltipNote}>(varies based on the lead's zip code)</div>
                            )}
                        </div>
                    }
                >
                    {currentBidDisplay}
                </TooltipDefinition>
            ) : (
                currentBidDisplay
            )}

            {CAN_INCREMENT_BID && (
                <Button
                    disabled={disabled}
                    className={clsx(styles.bidActionButton, styles['bidActionButton--increment'])}
                    hasIconOnly
                    iconDescription="Increase bid by $0.25"
                    onClick={() => updateBidValue(BID_ACTION.INCREASE)}
                    renderIcon={Add16}
                />
            )}
        </FormGroup>
    );
};

const calculateOutOfPocketCost = (bid, amount) => {
    const cost = Number(bid) - Number(amount);
    return dollarAmount(cost);
};

// Min bid constants
const DEFAULT = 1; //Non subsidy min bid for auto & health
const AUTO_CUSTOM_MIN_BID = 3;
const AUTO_PREMIUM_PREFERRED_MIN_BID = 3;
const HOME_PREMIER_MIN_BID = 16;
const HOME_CHOICE_MIN_BID = 14;
const HOME_SELECT_MIN_BID = 12;
const LIFE_TERM_WHOLE_LIFE_MIN_BID = 18;
const LIFE_FINAL_EXPENSE_MIN_BID = 14;
const LIFE_ANY_SOURCE_MIN_BID = 12;
const AUTO_SF_PREMIUM_PLUS_MIN_BID = 16;
const AUTO_SF_PREMIUM_MIN_BID = 13;
const AUTO_SF_PREFERRED_MIN_BID = 12;
const AUTO_BRANDED_PREMIUM_MIN_BID = 30;
const AUTO_BRANDED_PREFERRED_MIN_BID = 24;
const HOME_SF_PREMIER_MIN_BID = 16;
const HOME_SF_CHOICE_MIN_BID = 14;
const HOME_BRANDED_PREMIER_MIN_BID = 22;
const HOME_BRANDED_CHOICE_MIN_BID = 18;

const minBids = {
    premium: AUTO_PREMIUM_PREFERRED_MIN_BID,
    preferred: AUTO_PREMIUM_PREFERRED_MIN_BID,
    home_premier: HOME_PREMIER_MIN_BID,
    home_choice: HOME_CHOICE_MIN_BID,
    home_select: HOME_SELECT_MIN_BID,
    'term/whole_life': LIFE_TERM_WHOLE_LIFE_MIN_BID,
    final_expense: LIFE_FINAL_EXPENSE_MIN_BID,
    term_whole_life: LIFE_TERM_WHOLE_LIFE_MIN_BID,
    any: LIFE_ANY_SOURCE_MIN_BID,
    branded_premium_sf_subsidy: AUTO_BRANDED_PREMIUM_MIN_BID,
    branded_preferred_sf_subsidy: AUTO_BRANDED_PREFERRED_MIN_BID,
    premium_plus_sf_subsidy: AUTO_SF_PREMIUM_PLUS_MIN_BID,
    premium_sf_subsidy: AUTO_SF_PREMIUM_MIN_BID,
    preferred_sf_subsidy: AUTO_SF_PREFERRED_MIN_BID,
    home_premier_sf_subsidy: HOME_SF_PREMIER_MIN_BID,
    home_choice_sf_subsidy: HOME_SF_CHOICE_MIN_BID,
    branded_home_premier_sf_subsidy: HOME_BRANDED_PREMIER_MIN_BID,
    branded_home_choice_sf_subsidy: HOME_BRANDED_CHOICE_MIN_BID
};

// key will be shortName (default), channel (health), or channelType (life).
const getMinBid = (key) => minBids[key] || DEFAULT;

const BID_ACTION = {
    INCREASE: 'increase',
    DECREASE: 'decrease'
};

export { AUTO_CUSTOM_MIN_BID, BidField, getMinBid };
